import { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useNotify, useRefresh } from "react-admin";

import { adminApi } from "../../dataProvider";

type Wallet = { id: string; address: string; label: string | null };

type Preview = {
  from_address: string;
  to_address: string;
  amount_usdt: string | number;
  balance_usdt: string | number;
  balance_trx: string | number;
  energy_available: number;
  energy_needed: number;
  will_rent_energy: boolean;
  activated: boolean;
};

type Step = "input" | "confirm" | "sending" | "done";

export function SendDialog({ wallet, onClose }: { wallet: Wallet; onClose: () => void }) {
  const notify = useNotify();
  const refresh = useRefresh();
  const [step, setStep] = useState<Step>("input");
  const [toAddress, setToAddress] = useState("");
  const [amount, setAmount] = useState("");
  const [preview, setPreview] = useState<Preview | null>(null);
  const [result, setResult] = useState<{ tx_hash: string | null; status: string } | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const isValid = /^T[1-9A-HJ-NP-Za-km-z]{33}$/.test(toAddress.trim()) && Number(amount) > 0;

  const onPreview = async () => {
    setErr(null);
    try {
      const p = await adminApi<Preview>(`/admin/wallets/${wallet.id}/send-preview`, {
        method: "POST",
        json: { to_address: toAddress.trim(), amount_usdt: amount },
      });
      setPreview(p);
      setStep("confirm");
    } catch (e) {
      setErr(String(e));
    }
  };

  const onSend = async () => {
    setStep("sending");
    setErr(null);
    try {
      const r = await adminApi<{ tx_hash: string | null; status: string }>(
        `/admin/wallets/${wallet.id}/send`,
        { method: "POST", json: { to_address: toAddress.trim(), amount_usdt: amount } },
      );
      setResult(r);
      setStep("done");
      notify(r.tx_hash ? `Отправлено: ${r.tx_hash}` : "Транзакция создана", { type: "success" });
      refresh();
    } catch (e) {
      setErr(String(e));
      setStep("confirm");
    }
  };

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Отправить USDT</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Typography variant="caption" color="text.secondary">
            С кошелька <Box component="span" sx={{ fontFamily: "monospace" }}>{wallet.address}</Box>
            {wallet.label ? ` (${wallet.label})` : ""}
          </Typography>

          {step === "input" && (
            <>
              <TextField
                label="TRC20 адрес получателя"
                value={toAddress}
                onChange={(e) => setToAddress(e.target.value)}
                placeholder="T..."
                fullWidth
                autoFocus
              />
              <TextField
                label="Сумма USDT"
                type="number"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                fullWidth
                inputProps={{ step: "0.000001", min: "0" }}
              />
              {err && <Alert severity="error">{err}</Alert>}
            </>
          )}

          {step === "confirm" && preview && (
            <Box sx={{ "& > div": { display: "flex", justifyContent: "space-between", py: 0.5 } }}>
              <div><span>Куда</span><Box component="span" sx={{ fontFamily: "monospace" }}>{preview.to_address}</Box></div>
              <div><span>Сумма</span><b>{String(preview.amount_usdt)} USDT</b></div>
              <div><span>Баланс кошелька</span><span>{String(preview.balance_usdt)} USDT · {String(preview.balance_trx)} TRX</span></div>
              <div><span>Energy в наличии</span><span>{preview.energy_available.toLocaleString()}</span></div>
              <div><span>Energy нужно</span><span>{preview.energy_needed.toLocaleString()}</span></div>
              <div>
                <span>Аренда энергии у CatFee</span>
                <span>{preview.will_rent_energy ? "Да" : "Нет (хватает)"}</span>
              </div>
              <div><span>Получатель активирован</span><span>{preview.activated ? "Да" : "Нет"}</span></div>
              {Number(preview.amount_usdt) > Number(preview.balance_usdt) && (
                <Alert severity="error">Недостаточно USDT на кошельке</Alert>
              )}
              {err && <Alert severity="error">{err}</Alert>}
            </Box>
          )}

          {step === "sending" && (
            <Box sx={{ textAlign: "center", py: 3 }}>
              <Typography>Отправляем…</Typography>
              <Typography variant="caption" color="text.secondary">
                это может занять до 30 сек (broadcast + ожидание подтверждения)
              </Typography>
            </Box>
          )}

          {step === "done" && result && (
            <Alert severity="success">
              Транзакция: {result.tx_hash ?? "(hash ещё не доступен)"} <br />
              Статус: <b>{result.status}</b>
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        {step === "input" && (
          <>
            <Button onClick={onClose}>Отмена</Button>
            <Button variant="contained" disabled={!isValid} onClick={onPreview}>Превью</Button>
          </>
        )}
        {step === "confirm" && (
          <>
            <Button onClick={() => setStep("input")}>Назад</Button>
            <Button color="error" variant="contained" onClick={onSend}>Подтвердить отправку</Button>
          </>
        )}
        {(step === "sending" || step === "done") && (
          <Button onClick={onClose}>Закрыть</Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
